// Cours premium n°5 — "Consolider • S'adapter • Rester régulier • Se rapprocher du niveau examen"
// Circulation A2 — 14 chapitres (sans visuels, trame identique aux cours 1/2/3/4)
// Cours 1 : je découvre → Cours 2 : j'observe et anticipe → Cours 3 : je construis ma conduite
// → Cours 4 : je conduis en autonomie → Cours 5 : je vérifie que mon autonomie tient dans un parcours complexe

export const cours = {
  slug: 'cinquieme-circulation-a2',
  title: 'Ma 5ᵉ circulation A2',
  subtitle: 'Consolider • S\'adapter • Rester régulier • Se rapprocher du niveau examen',
  duree: '2 heures de conduite',
  mantra: 'OBSERVER → HIÉRARCHISER → ANTICIPER → DÉCIDER → AGIR',
  intro: "La session 4 t'a appris à conduire sans être constamment guidé. La session 5 monte encore d'un cran : elle vérifie que cette autonomie reste solide lorsque le parcours devient complexe et se rapproche des exigences réelles de l'épreuve circulation A2. L'inspecteur évalue la prise d'information, l'allure, la réglementation, la communication, le partage de la chaussée, les espaces de sécurité, mais aussi l'analyse des situations, l'adaptation et la conduite autonome — sur des environnements variés, avec environ 5 minutes de conduite autonome.",
  sections: [
    {
      num: '01',
      kicker: 'Contrat de travail',
      title: 'Ton bilan n°4 devient ton contrat de travail',
      lead: "Nous repartons de ton dernier bilan. Aujourd'hui, tu dois être capable d'identifier toi-même ce qui devient ton fil conducteur pendant les 2 heures.",
      type: 'timeline',
      steps: [
        { time: 'Acquis', label: 'CE QUE TU MAÎTRISES', desc: 'Tu identifies les compétences désormais solides sur lesquelles tu peux t\'appuyer.' },
        { time: 'Fragile', label: 'CE QUI RESTE FRAGILE', desc: 'Tu nommes précisément les situations où tu manques encore de régularité.' },
        { time: 'Cible', label: 'CE QUE TU VEUX AMÉLIORER', desc: 'Tu choisis un objectif prioritaire, mesurable dès aujourd\'hui.' },
      ],
      retenir: 'TON OBJECTIF DEVIENT TON FIL CONDUCTEUR PENDANT LES 2 HEURES.',
    },
    {
      num: '02',
      kicker: 'Démarrage assumé',
      title: 'Tu prends ta conduite en main dès le départ',
      lead: "Cette fois, l'autonomie commence immédiatement. Installation, départ, insertion dans la circulation, observation, allure, positionnement… Tu dois retrouver rapidement ton niveau de conduite, sans temps d'adaptation prolongé.",
      type: 'cards3',
      cards: [
        { title: 'INSTALLATION & DÉPART', desc: 'Réglages, contrôles, insertion : rythmés, précis, autonomes.' },
        { title: 'OBSERVATION & ALLURE', desc: 'Regard actif dès les premiers mètres, allure adaptée à l\'environnement.' },
        { title: 'POSITIONNEMENT', desc: 'Choisi immédiatement, sans attendre une consigne extérieure.' },
      ],
      retenir: 'JE NE COMMENCE PLUS À APPRENDRE À CONDUIRE. JE COMMENCE À MONTRER CE QUE JE SAIS FAIRE.',
    },
    {
      num: '03',
      kicker: 'Radio discrète',
      title: 'Tu conduis sans attendre la radio',
      lead: "L'enseignant reste derrière le groupe et assure naturellement la sécurité et la pédagogie. Mais la radio devient plus discrète. Elle sert principalement à :",
      type: 'list',
      list: [
        { label: 'DONNER UNE DIRECTION', desc: 'Un point de destination, pas une trajectoire.' },
        { label: 'ORGANISER LE GROUPE', desc: 'Ordre de passage, regroupement, séparation.' },
        { label: 'APPORTER UNE CORRECTION UTILE', desc: 'Sur un point précis, quand c\'est réellement nécessaire.' },
        { label: 'INTERVENIR POUR LA SÉCURITÉ', desc: 'Uniquement lorsqu\'un risque immédiat apparaît.' },
      ],
      retenir: 'ENTRE DEUX INDICATIONS : C\'EST TOI QUI CONDUIS.',
    },
    {
      num: '04',
      kicker: 'Itinéraire autonome',
      title: 'Tu suis un itinéraire en autonomie',
      lead: "Nous renforçons cette compétence. Ton enseignant peut annoncer : « À partir de maintenant, suis la direction Grenoble. » Tu recherches seul les panneaux. Mais attention : ta priorité reste la conduite, pas la destination.",
      type: 'cards3',
      cards: [
        { title: 'JE CHERCHE', desc: 'Je repère la signalisation directionnelle sans quitter la route des yeux.' },
        { title: 'JE CONDUIS D\'ABORD', desc: 'La conduite reste ma priorité absolue, pas la destination.' },
        { title: 'JE CORRIGE EN SÉCURITÉ', desc: 'Si je rate une direction, je continue en sécurité et je corrige plus loin.' },
      ],
      body: "Cette logique prépare directement la phase d'autonomie d'environ 5 minutes prévue lors de l'épreuve A2.",
      retenir: 'UNE ERREUR D\'ITINÉRAIRE SE CORRIGE. UNE DÉCISION DANGEREUSE POUR LA RATTRAPER, NON.',
    },
    {
      num: '05',
      kicker: 'Changements de contexte',
      title: 'Tu détectes les changements de contexte',
      lead: "Tu quittes une ville. La route s'élargit. La limitation change. La visibilité augmente. Puis apparaît un virage, une intersection ou une nouvelle agglomération. À chaque transition, deux questions immédiates :",
      type: 'timeline',
      steps: [
        { time: '1', label: 'JE QUITTE UNE VILLE', desc: 'La route s\'élargit, la limitation évolue, la visibilité s\'ouvre.' },
        { time: '2', label: 'UN VIRAGE APPARAÎT', desc: 'Regard, allure et positionnement doivent s\'ajuster avant l\'entrée.' },
        { time: '3', label: 'UNE INTERSECTION SE PRÉSENTE', desc: 'Priorités, communication et vigilance montent d\'un cran.' },
        { time: '4', label: 'UNE NOUVELLE AGGLOMÉRATION', desc: 'Retour à l\'allure urbaine, aux usagers vulnérables, aux espaces réduits.' },
      ],
      retenir: 'QU\'EST-CE QUI CHANGE ? QU\'EST-CE QUE JE DOIS CHANGER DANS MA CONDUITE ?',
    },
    {
      num: '06',
      kicker: 'Marges',
      title: 'Tu construis tes marges avant d\'en avoir besoin',
      lead: "Le motard autonome ne cherche pas seulement à éviter le danger : il se donne du temps et de l'espace en permanence. Tes marges se construisent avant que la situation ne devienne difficile.",
      type: 'list',
      list: [
        { label: 'DISTANCE DEVANT', desc: 'Suffisante pour observer, décider et freiner sans à-coup.' },
        { label: 'ESPACE LATÉRAL', desc: 'Sur les côtés, en particulier près des véhicules stationnés.' },
        { label: 'VISIBILITÉ', desc: 'Cherchée activement : je me place pour voir, et pour être vu.' },
        { label: 'POSITIONNEMENT', desc: 'Choisi pour ouvrir mes options et anticiper la sortie.' },
        { label: 'ALLURE', desc: 'Adaptée en amont, pas ajustée en catastrophe.' },
        { label: 'FREINAGE / ÉVITEMENT', desc: 'Rester capable de freiner ou de dévier à tout moment.' },
      ],
      retenir: 'TA MARGE DE SÉCURITÉ EST TON CAPITAL D\'ACTION.',
    },
    {
      num: '07',
      kicker: 'Situations complexes',
      title: 'Tu gères les situations complexes',
      lead: "Nous pouvons maintenant rechercher des situations associant plusieurs éléments à traiter en même temps. Tu apprends à hiérarchiser ce qui compte le plus, puis à agir.",
      type: 'cards3',
      cards: [
        { title: 'INTERSECTION MIXTE', desc: 'Intersection + véhicule stationné + piéton en approche.' },
        { title: 'VIRAGE À RISQUE', desc: 'Virage + mauvaise visibilité + véhicule venant en sens inverse.' },
        { title: 'INSERTION DENSE', desc: 'Circulation dense + changement de voie + insertion à faire.' },
      ],
      retenir: 'OBSERVER → HIÉRARCHISER → ANTICIPER → DÉCIDER → AGIR.',
    },
    {
      num: '08',
      kicker: 'Décision assumée',
      title: 'Tu prends une décision… et tu l\'assumes',
      lead: "S'engager ? Attendre ? Dépasser ? Renoncer ? Changer de voie ? Conserver sa position ? Tu dois prendre une décision suffisamment tôt. Mais une décision n'est jamais définitive : si la situation évolue, tu réévalues et tu t'adaptes.",
      type: 'list',
      list: [
        { label: 'S\'ENGAGER', desc: 'Quand l\'espace, la visibilité et la sécurité sont réunis.' },
        { label: 'ATTENDRE', desc: 'Quand un doute subsiste, tu conserves ta marge.' },
        { label: 'DÉPASSER', desc: 'Uniquement si la visibilité et la loi le permettent réellement.' },
        { label: 'RENONCER', desc: 'Ce n\'est pas un échec : c\'est un signe d\'analyse.' },
        { label: 'CHANGER DE VOIE', desc: 'Avec contrôles complets, communication et anticipation.' },
        { label: 'RÉÉVALUER', desc: 'La situation évolue ? Je remets ma décision à jour.' },
      ],
      retenir: 'CETTE CAPACITÉ D\'ANALYSE ET D\'ADAPTATION FAIT PARTIE DE L\'ÉVALUATION A2.',
    },
    {
      num: '09',
      kicker: 'Prévisibilité',
      title: 'Tu restes prévisible pour les autres',
      lead: "Un bon motard ne se contente pas de comprendre les autres. Les autres doivent aussi pouvoir le comprendre. Ton comportement doit être lisible en permanence.",
      type: 'list',
      list: [
        { label: 'POSITIONNEMENT', desc: 'Tu te places sur la voie qui prépare visiblement ton intention.' },
        { label: 'ALLURE', desc: 'Cohérente avec le contexte, sans ralentissement inexpliqué.' },
        { label: 'CLIGNOTANTS', desc: 'Anticipés, jamais posés en dernière seconde.' },
        { label: 'FEUX STOP', desc: 'Ils annoncent tes freinages : tu freines progressivement.' },
        { label: 'DISTANCES', desc: 'Constantes : elles rassurent aussi celui qui te suit.' },
        { label: 'TRAJECTOIRE', desc: 'Nette et sans hésitation, elle est lue par les autres usagers.' },
      ],
      retenir: 'CIRCULER EN SÉCURITÉ : SANS GÊNER, SANS SURPRENDRE, SANS ÊTRE SURPRIS.',
    },
    {
      num: '10',
      kicker: 'Voie rapide',
      title: 'Tu gères ta conduite sur voie rapide',
      lead: "Lorsque l'environnement le permet, cette session consolide ta conduite sur route à chaussée séparée ou voie assimilée. L'objectif n'est pas simplement de « prendre la voie rapide », mais de comprendre comment t'intégrer dans un flux plus rapide.",
      type: 'list',
      list: [
        { label: 'VOIE D\'ACCÉLÉRATION', desc: 'Utilisée entièrement pour atteindre l\'allure du flux.' },
        { label: 'CONTRÔLES', desc: 'Rétros et arrière : construits, pas expédiés.' },
        { label: 'ANGLE MORT', desc: 'Vérifié systématiquement avant tout déport.' },
        { label: 'ADAPTATION DE L\'ALLURE', desc: 'Tu prends l\'allure du flux, tu ne l\'imposes pas.' },
        { label: 'INSERTION', desc: 'Fluide, sans forcer, avec communication claire.' },
        { label: 'DISTANCES', desc: 'Plus grandes qu\'en ville : la vitesse impose la marge.' },
        { label: 'CHANGEMENT DE VOIE', desc: 'Anticipé, contrôlé, exécuté sans à-coup.' },
        { label: 'DÉPASSEMENT', desc: 'Complet : allure, position, contrôles, retour à droite.' },
        { label: 'VOIE DE DÉCÉLÉRATION', desc: 'Utilisée avant le ralentissement, pas pendant.' },
      ],
      retenir: 'COMMENT T\'INTÉGRER DANS UN FLUX PLUS RAPIDE, PAS SIMPLEMENT « Y ROULER ».',
    },
    {
      num: '11',
      kicker: 'Récupération',
      title: 'Tu restes performant même après une erreur',
      lead: "C'est un point essentiel avant l'examen. Tu rates une direction. Tu cales. Tu choisis mal ton rapport. Tu hésites. Ton enseignant te fait une remarque. La séance continue. Ta capacité à retrouver immédiatement une conduite sûre est ce qui fait la différence le jour J.",
      type: 'list',
      list: [
        { label: 'JE CONSTATE', desc: 'Ce qui vient de se passer, sans dramatiser.' },
        { label: 'JE COMPRENDS', desc: 'Ce qui m\'a fait faire cette erreur, rapidement.' },
        { label: 'JE CORRIGE', desc: 'Je remets ma conduite en ordre : allure, position, regard.' },
        { label: 'JE REPARS', desc: 'Sans traîner la faute dans les minutes suivantes.' },
        { label: 'JE RESTE CONCENTRÉ', desc: 'L\'erreur suivante ne doit pas naître de la précédente.' },
      ],
      retenir: 'UNE ERREUR NE DOIT PAS EN CRÉER UNE DEUXIÈME.',
    },
    {
      num: '12',
      kicker: 'Séquence examen',
      title: 'Tu effectues une séquence type « niveau examen »',
      lead: "C'est ici que la session 5 prend tout son sens. Pendant une partie du parcours, ton enseignant intervient le moins possible. Tu rencontres successivement plusieurs environnements. L'objectif n'est pas de te « piéger » : il est d'observer si tes compétences restent stables sans aide.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'AGGLOMÉRATION', desc: 'Usagers vulnérables, priorités, allures faibles.' },
        { time: '2', label: 'INTERSECTIONS', desc: 'Priorités, positionnement, communication.' },
        { time: '3', label: 'CHANGEMENTS DE DIRECTION', desc: 'Anticipés, avec observation complète.' },
        { time: '4', label: 'ROUTE OUVERTE', desc: 'Allure supérieure, distances plus grandes.' },
        { time: '5', label: 'VIRAGES', desc: 'Trajectoire, allure, regard porté loin.' },
        { time: '6', label: 'INSERTION', desc: 'Dans un flux, sans forcer et sans hésiter.' },
        { time: '7', label: 'VOIE RAPIDE', desc: 'Contrôles, angle mort, distances, dépassement propre.' },
        { time: '8', label: 'CHANGEMENT D\'ENVIRONNEMENT', desc: 'Transition détectée et adaptation immédiate.' },
        { time: '9', label: 'AUTONOMIE', desc: 'Séquence guidée uniquement par ta propre conduite.' },
      ],
      retenir: 'EST-CE QUE TES COMPÉTENCES RESTENT STABLES QUAND PERSONNE NE T\'AIDE ?',
    },
    {
      num: '13',
      kicker: 'Débriefing autonome',
      title: 'Tu fais ton propre débriefing',
      lead: "Après cette séquence, l'enseignant ne parle pas en premier. Trois questions simples te sont posées. On recherche une vraie conscience du risque et de tes propres capacités, pas seulement l'exécution mécanique des consignes.",
      type: 'list',
      list: [
        { label: '« QU\'EST-CE QUI ÉTAIT RÉUSSI ? »', desc: 'Tu identifies concrètement ce que tu as bien fait.' },
        { label: '« QUELLE SITUATION T\'A POSÉ LE PLUS DE DIFFICULTÉS ? »', desc: 'Tu nommes précisément le moment délicat.' },
        { label: '« SI TU RECOMMENÇAIS MAINTENANT, QUE CHANGERAIS-TU ? »', desc: 'Tu proposes toi-même l\'ajustement.' },
      ],
      retenir: 'ON CHERCHE UNE VRAIE CONSCIENCE DU RISQUE. PAS L\'EXÉCUTION MÉCANIQUE.',
    },
    {
      num: '14',
      kicker: 'Bilan N°5',
      title: 'Ton bilan n°5 : suis-je proche du niveau attendu ?',
      lead: "Cette fois, la grille change. On ne demande plus uniquement « est-ce que je sais faire ? », mais « est-ce que je sais le faire seul, correctement et régulièrement ? »",
      type: 'bilan',
      questions: [
        'Je prends l\'information suffisamment tôt.',
        'J\'adapte mon allure sans attendre une consigne.',
        'J\'applique correctement la réglementation.',
        'Je communique avec les autres usagers.',
        'Je partage correctement la chaussée.',
        'Je maintiens mes espaces de sécurité.',
        'J\'analyse les situations.',
        'Je m\'adapte lorsque la situation évolue.',
        'Je conduis de manière autonome.',
        'Je prends en compte les usagers vulnérables.',
        'Je reste régulier pendant toute la conduite.',
        'Je sais récupérer après une erreur.',
      ],
      blocks: [
        { title: 'JE MAÎTRISE SEUL', color: 'green' },
        { title: 'JE CONSOLIDE', color: 'amber' },
        { title: 'OBJECTIF SESSION N°6', color: 'red' },
      ],
      retenir: 'ÊTRE PRÊT POUR L\'EXAMEN, CE N\'EST PAS RÉUSSIR UNE FOIS. C\'EST BIEN CONDUIRE, SEUL ET DE FAÇON RÉGULIÈRE.',
    },
  ],
};
